import {
  Badge,
  ButtonList,
  Divider,
  DropdownButton,
  EmptyState,
  FlexBox,
  SelectionList,
} from '@dixa/lib-shared-habla';
import { AiSettingsVersionsQuery } from 'lib-knowledge-management-common/graphql';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './versionSwitcher.module.scss';

export type AiSettingsVersionSummary = NonNullable<
  AiSettingsVersionsQuery['aiSettingsVersions']
>[number];

const DRAFT = 'draft';

function LiveBadge() {
  return (
    <Badge size="small" variant="success">
      Live
    </Badge>
  );
}

/** Moves between the draft and the saved versions, as in the MIM settings. */
export function VersionSwitcher({
  versions,
  loading,
  currentVersionId,
}: {
  versions: AiSettingsVersionSummary[];
  loading: boolean;
  /** Unset while on the draft. */
  currentVersionId: string | undefined;
}): JSX.Element {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const current = versions.find((version) => version.id === currentVersionId);

  const handleChange = (value: string) => {
    setIsOpen(false);
    navigate(
      value === DRAFT
        ? routeGenerator.aiSuggestionsSettings()
        : routeGenerator.aiSuggestionsVersion({ versionId: value }),
    );
  };

  return (
    <DropdownButton
      fixed
      show={isOpen}
      onToggle={setIsOpen}
      buttonProps={{ variant: 'secondary' }}
      dropdown={
        <div className={styles.dropdown}>
          <SelectionList
            className={styles.list}
            selectedValues={[currentVersionId ?? DRAFT]}
            isActive={isOpen}
            defaultActiveItemIndex={0}
            onChange={(change) => handleChange(change.selectedItem.value)}
          >
            <SelectionList.Item value={DRAFT}>Draft</SelectionList.Item>
            <SelectionList.Section title="Versions">
              {versions.map((version) => (
                <SelectionList.Item key={version.id} value={version.id}>
                  <FlexBox gap="200" alignItems="center">
                    v{version.version} {version.name}
                    {version.isActive && <LiveBadge />}
                  </FlexBox>
                </SelectionList.Item>
              ))}
            </SelectionList.Section>
          </SelectionList>

          {!loading && versions.length === 0 && (
            <EmptyState
              className={styles.emptyState}
              size="small"
              title="You have no versions yet"
              description="Versions will appear here once created"
              icon={null}
            />
          )}

          <Divider />

          <ButtonList>
            <ButtonList.Item
              icon="cog"
              onClick={() => {
                setIsOpen(false);
                navigate(routeGenerator.aiSuggestionsVersions());
              }}
            >
              Manage versions
            </ButtonList.Item>
          </ButtonList>
        </div>
      }
    >
      <FlexBox gap="200" alignItems="center">
        {current ? current.name : 'Draft'}
        {current?.isActive && <LiveBadge />}
      </FlexBox>
    </DropdownButton>
  );
}
